import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';

import { useIntersectionObserver, useAutoSize } from '@trqk/hooks';
import {
    leftNavConstants,
    LeftNavContentSection,
    LeftNavHeader,
    LoadingSpinner,
} from '@trqk/components';
import { locale } from '@trqk/utils';

import { useAnchorLinks, usePaymentPlanStatus } from '../../../hooks';
import { WebLayout } from '../../templates';
import { sections, heights, sectionIds, sectionMap } from './constants';
import { HASHES, MAIN_CONTENT_ID, PATHS } from '../../../constants';
import { TotalRoyalties } from './components';

const { AUTO_SIZE_OFFSETS, HEIGHTS, WIDTHS } = leftNavConstants;

/**
 * Displays reports, including the Gap Analysis and Total Royalties reports.
 */
export const Reports = (): JSX.Element => {
    const history = useHistory();
    const [refreshTotalRoyaltiesData, setRefreshTotalRoyaltiesData] =
        useState(0);
    const { setActiveScrollObservers } = useAnchorLinks();

    const onSetActiveSectionId = useCallback(
        (id: string) => {
            // When the user scrolls to a new section of the page (i.e. a new report), replace the
            // updates the URL hash when the modal is not open.
            if (
                // Don't update the hash if the Create Gap Analysis modal is open
                history.location.pathname === PATHS.REPORTS &&
                history.location.hash !== id
            ) {
                history.replace({
                    search: history.location.search,
                    hash: id,
                });
            }

            if (id === HASHES.REPORTS.TOTAL_ROYALTIES) {
                // Refresh total royalties data by running the `getCustomerStatementHistory` query
                // when the user navigates to the Total Royalties report
                setRefreshTotalRoyaltiesData(Date.now());
            }
        },
        [
            history.location.hash,
            history.location.search,
            history.location.pathname,
        ],
    );

    const { activeSectionId, observe } = useIntersectionObserver({
        defaultSectionId: sections?.[0]?.id ?? sectionMap.totalRoyalties.id,
        sectionIds,
        observerOptions: {
            root: document.querySelector(MAIN_CONTENT_ID),
            rootMargin: '0px',
            threshold: 0.8,
        },
        onSetActiveSectionId,
    });

    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
        {
            [sectionMap.totalRoyalties.id]: true,
        },
    );

    const showLoadingSpinner = Object.values(loadingStates).some(
        (sectionIsLoading: boolean) => sectionIsLoading,
    );

    const contentAreaRef = useRef<HTMLDivElement>(null);

    const { showFreeTrialFooter } = usePaymentPlanStatus();
    const { width } = useAutoSize({
        elementRef: contentAreaRef,
        resizeRate: 400,
        rightOffset: AUTO_SIZE_OFFSETS.RIGHT,
        bottomOffset: showFreeTrialFooter
            ? HEIGHTS.ACCOUNT_DROPDOWN + heights.autoSizeOffsetBottom
            : heights.autoSizeOffsetBottom,
        minHeight: heights.minSectionHeight,
        minWidth: WIDTHS.MAIN_XL - AUTO_SIZE_OFFSETS.MIN_WIDTH,
    });

    const mainWidth = Math.max(WIDTHS.MAIN_XL, width);

    const {
        location: { pathname },
    } = history;

    useEffect(() => {
        if (!showLoadingSpinner) {
            setActiveScrollObservers(
                (prev: Record<string, boolean>): Record<string, boolean> => ({
                    ...prev,
                    [pathname]: false,
                }),
            );
        }
    }, [pathname, showLoadingSpinner]);

    return (
        <WebLayout
            headerPadding="sm"
            header={{
                content: (
                    <LeftNavHeader
                        activeSectionId={activeSectionId}
                        sections={sections}
                    >
                        <h3 data-cy="reports-title">
                            {locale.enUS.REPORTS.TITLE}
                        </h3>
                    </LeftNavHeader>
                ),
                wrapperClass: 'semi-transparent-header',
            }}
            mainWidth={mainWidth}
        >
            <div className="flex flex-col w-full" ref={contentAreaRef}>
                <div className="mb-10 w-full">
                    {/*
                        We need to mount the Total Royalties section so that it can load its data
                        while we are showing the loading spinner, but keep it hidden until the
                        Total Royalties chart can be rendered.
                    */}
                    {showLoadingSpinner && (
                        <div
                            style={{ height: heights.minSectionHeight }}
                            className="flex justify-center items-center"
                        >
                            <LoadingSpinner />
                        </div>
                    )}
                    <div
                        className={cx({
                            hidden: showLoadingSpinner,
                        })}
                    >
                        <LeftNavContentSection
                            id={sectionMap.totalRoyalties.id}
                            ref={observe}
                        >
                            <TotalRoyalties
                                refresh={refreshTotalRoyaltiesData}
                                sectionId={sectionMap.totalRoyalties.id}
                                setLoadingStates={setLoadingStates}
                            />
                        </LeftNavContentSection>
                    </div>
                </div>
            </div>
        </WebLayout>
    );
};
